import { defaultCopyTitle } from '~entities/classes/helpers/build-class-details-form';
import type { ClassDetails } from '~entities/classes/modal/class';

import { extractDateOnly } from '~shared/helpers/datetime';
import { calculateDateDifference } from '~shared/utils/date-time';

export type CopyOfferingEditValues = {
  title: string;
  startDate: string;
  endDate: string;
  registrationStartDate: string;
  tuitionFee: string;
  costumeFee: string;
  registrationFee: string;
  tuitionBillingCycle: string;
  sessionBillingDate?: string;
};

export type CopyOfferingFieldOverrides = {
  title: string;
  startDate: string;
  endDate: string;
  registrationStartDate: string;
  tuitionFee: number;
  costumeFee: number;
  registrationFeeAmount: number;
};

export type CopyOfferingEditErrors = Partial<
  Record<
    | 'title'
    | 'startDate'
    | 'endDate'
    | 'registrationStartDate'
    | 'tuitionFee'
    | 'costumeFee'
    | 'registrationFee',
    string
  >
>;

const AMOUNT_PATTERN = /^\d+(\.\d{1,2})?$/;

function feeString(value: number | undefined | null): string {
  if (value === undefined || value === null || Number.isNaN(value)) {
    return '0';
  }
  return String(value);
}

function normalizeBillingCycle(cycle: string | undefined): string {
  return (cycle ?? '').trim().toLowerCase();
}

export function isHourlyBillingCycle(cycle: string | undefined): boolean {
  return normalizeBillingCycle(cycle) === 'hourly';
}

export function buildCopyOfferingEditValues(
  offering: ClassDetails,
): CopyOfferingEditValues {
  return {
    title: defaultCopyTitle(offering.title),
    startDate: extractDateOnly(offering.startDate) || '',
    endDate: extractDateOnly(offering.endDate) || '',
    registrationStartDate: offering.registrationStartDate
      ? extractDateOnly(offering.registrationStartDate) || ''
      : '',
    tuitionFee: feeString(offering.tuitionFee),
    costumeFee: feeString(offering.costumeFee ?? 0),
    registrationFee: feeString(offering.registrationFeeAmount),
    tuitionBillingCycle: offering.tuitionBillingCycle ?? '',
    sessionBillingDate: offering.session?.billingDate,
  };
}

function validateAmount(
  value: string,
  {
    required,
    requiredMessage,
    negativeMessage,
    invalidMessage,
  }: {
    required?: boolean;
    requiredMessage: string;
    negativeMessage: string;
    invalidMessage: string;
  },
): string | undefined {
  const trimmed = value.trim();
  if (!trimmed) {
    return required ? requiredMessage : undefined;
  }
  const numeric = Number(trimmed);
  if (!Number.isNaN(numeric) && numeric < 0) {
    return negativeMessage;
  }
  if (!AMOUNT_PATTERN.test(trimmed)) {
    return invalidMessage;
  }
  return undefined;
}

export function validateCopyOfferingEditValues(
  values: CopyOfferingEditValues,
): CopyOfferingEditErrors {
  const errors: CopyOfferingEditErrors = {};

  if (!values.title.trim()) {
    errors.title = 'Class name is required';
  }

  if (!values.startDate) {
    errors.startDate = 'Start date is required';
  }

  if (!values.endDate) {
    errors.endDate = 'End date is required';
  }

  if (values.startDate && values.endDate) {
    const startDate = new Date(`${values.startDate}T00:00:00`);
    const endDate = new Date(`${values.endDate}T00:00:00`);
    if (!(endDate > startDate)) {
      errors.endDate = 'End date must be after start date';
    }

    const cycle = normalizeBillingCycle(values.tuitionBillingCycle);
    const span = calculateDateDifference(values.startDate, values.endDate);
    if (cycle === 'weekly' && span < 7) {
      errors.startDate = 'Class must be at least 7 days long';
    } else if (cycle === 'bi-weekly' && span < 14) {
      errors.startDate = 'Class must be at least 14 days long';
    } else if (cycle === 'monthly' && span < 30) {
      errors.startDate = 'Class must be at least 30 days long';
    }
  }

  if (values.startDate && values.sessionBillingDate) {
    const billing = new Date(values.sessionBillingDate);
    const start = new Date(`${values.startDate}T00:00:00`);
    billing.setHours(0, 0, 0, 0);
    start.setHours(0, 0, 0, 0);
    if (billing.getTime() > start.getTime()) {
      const formatted = billing.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
      errors.startDate = `Class start date must be on or after session billing date (${formatted})`;
    }
  }

  if (!isHourlyBillingCycle(values.tuitionBillingCycle)) {
    const membershipError = validateAmount(values.registrationFee, {
      requiredMessage: 'Registration fee cannot be negative',
      negativeMessage: 'Registration fee cannot be negative',
      invalidMessage: 'Please enter a valid amount',
    });
    if (membershipError) errors.registrationFee = membershipError;

    const costumeError = validateAmount(values.costumeFee, {
      requiredMessage: 'Fee cannot be negative',
      negativeMessage: 'Fee cannot be negative',
      invalidMessage: 'Please enter a valid amount',
    });
    if (costumeError) errors.costumeFee = costumeError;

    const tuitionError = validateAmount(values.tuitionFee, {
      required: true,
      requiredMessage: 'Tuition fee is required',
      negativeMessage: 'Fee cannot be negative',
      invalidMessage: 'Enter a valid amount',
    });
    if (tuitionError) errors.tuitionFee = tuitionError;
  }

  return errors;
}

export function hasCopyOfferingEditErrors(
  errors: CopyOfferingEditErrors,
): boolean {
  return Object.keys(errors).length > 0;
}

export function toCopyOfferingFieldOverrides(
  values: CopyOfferingEditValues,
): CopyOfferingFieldOverrides {
  const hourly = isHourlyBillingCycle(values.tuitionBillingCycle);
  return {
    title: values.title.trim(),
    startDate: values.startDate,
    endDate: values.endDate,
    registrationStartDate: values.registrationStartDate,
    tuitionFee: hourly ? 0 : Number(values.tuitionFee),
    costumeFee: hourly ? 0 : Number(values.costumeFee || 0),
    registrationFeeAmount: hourly ? 0 : Number(values.registrationFee || 0),
  };
}

export function toApiDate(date: string): string {
  if (!date) return '';
  if (date.includes('T')) return date;
  return `${date}T00:00:00.000Z`;
}
